/** Only an explicitly enabled, unambiguous live Entra provider may start without a click. */
export function automaticSsoProvider<T extends { kind: string; automaticSignIn?: boolean; simulated?: boolean }>(providers: T[]): T | null {
  return providers.length === 1 && providers[0].kind === "EntraId" && providers[0].automaticSignIn === true && !providers[0].simulated ? providers[0] : null;
}

const MANUAL_OR_CALLBACK_PARAMS = ["reason", "manual", "code", "state", "error"];

/** Only a fresh document at this application's root starts another automatic sign-in visit. */
export function isAutomaticSsoRootArrival(pathname: string, search: string, appBase: string): boolean {
  const root = appBase.replace(/\/+$/, "");
  const query = new URLSearchParams(search);
  return (pathname === `${root}/` || (root !== "" && pathname === root)) &&
    !MANUAL_OR_CALLBACK_PARAMS.some((name) => query.has(name));
}

export function automaticSsoAttemptKey(appBase: string): string {
  return `ncp.sso.automatic:${appBase}`;
}

/** One attempt per tab/application visit, recorded before the network call to survive remounts and redirects. */
export function claimAutomaticSsoAttempt(storage: Pick<Storage, "getItem" | "setItem">, key: string, search: string, blocked: boolean): boolean {
  const query = new URLSearchParams(search);
  if (blocked || MANUAL_OR_CALLBACK_PARAMS.some((name) => query.has(name))) return false;
  try {
    if (storage.getItem(key)) return false;
    storage.setItem(key, "1");
    return true;
  } catch {
    return false; // Manual sign-in remains available when browser storage is unavailable.
  }
}

/** Hide only expected no-session/interaction responses after the server has validated and consumed our state. */
export function isExpectedSilentSsoFailure(silent: boolean | undefined, returnedError: string | null, validatedCode: string | undefined): boolean {
  return silent === true && returnedError === validatedCode &&
    ["login_required", "interaction_required", "consent_required", "account_selection_required"].includes(validatedCode ?? "");
}
